import React from "react";
import Framework from "@beardeddevops/react.framework";
import Types from 'constants/Types';
import {Tooltip} from "@material-ui/core";
import {Button} from "reactstrap";
import {Collections, Objects, Elements} from "components";
import BarcodeReader from 'react-barcode-reader';
import APPCONFIG from "../../constants/Config";
import API from "@beardeddevops/react.api";
import {toast} from "react-toastify";
import CustomerComponent from "./Parts/CustomerComponent";
import {guardOpenWorkOrderBeforeAdd} from "../../utils/openWorkOrderWarning";

// Layaway-scoped totals reducer. Layaways currently DO NOT support transaction-level discount_amount /
// discount_percentage / trade-ins, so this file deliberately diverges from the canonical "checkout totals"
// order of operations. The cart spec lives on `Checkout.recalculateTotals` (frontend, JSDoc) and
// `\Abstracts\Utilities::buildSectionFromLines` (backend); when layaway adopts the same shape, mirror those
// steps here too.

function layawayNum(v) {
	const n = Number(v);
	return Number.isFinite(n) ? n : 0;
}

function layawayRate(location, camel, pascal) {
	return layawayNum(location?.[camel] ?? location?.[pascal]);
}

function layawayLocationCombinedRate(location) {
	return (
		layawayRate(location, 'tax_rate_city', 'TaxRateCity') +
		layawayRate(location, 'tax_rate_county', 'TaxRateCounty') +
		layawayRate(location, 'tax_rate_special', 'TaxRateSpecial') +
		layawayRate(location, 'tax_rate_state', 'TaxRateState')
	);
}

function layawayLocationFflRate(location) {
	return layawayRate(location, 'tax_rate_fflboss', 'TaxRateFflboss');
}

function layawayEntrySkuClass(entry) {
	const sku = entry?.information?.skus ?? entry?.information?.Skus ?? null;
	if (!sku) return null;
	return sku.sku_classes ?? sku.skuClasses ?? sku.SkuClasses ?? null;
}

function layawayBool(v) {
	if (v === true || v === 1 || v === '1' || v === 'true') return true;
	if (v === false || v === 0 || v === '0' || v === 'false') return false;
	return !!v;
}

function layawayEntryQuantity(entry) {
	if (entry?.quantity != null) return layawayNum(entry.quantity) || 1;
	if (entry?.information?.quantity != null) return layawayNum(entry.information.quantity) || 1;
	return 1;
}

function layawayEntryUnitCost(entry) {
	if (entry?.price != null) return layawayNum(entry.price);
	if (entry?.cost != null) return layawayNum(entry.cost);
	if (entry?.information?.price != null) return layawayNum(entry.information.price);
	if (entry?.information?.skus?.price_retail != null) return layawayNum(entry.information.skus.price_retail);
	if (entry?.information?.price_list != null) return layawayNum(entry.information.price_list);
	return 0;
}

function layawayEntryDiscount(entry) {
	return layawayNum(entry?.discount_amount ?? 0);
}

function layawayEntryTaxable(entry) {
	if (entry?.taxable != null) return layawayBool(entry.taxable);
	if (entry?.information?.taxable != null) return layawayBool(entry.information.taxable);
	const sku = entry?.information?.skus ?? entry?.information?.Skus ?? null;
	if (sku?.taxable != null) return layawayBool(sku.taxable);
	return true;
}

/**
 * Full layaway totals + tax buckets for persistence (tax_json) and UI.
 * @param {array} entries this.state.entries
 * @param {object} location rootState.location
 * @param {boolean} isFflLinked rootState.ffl_boss_linked
 * @param {number} deposit cumulative deposit for remaining only
 */
function computeLayawayTaxTotals(entries, location, isFflLinked, deposit) {
	const safeEntries = Array.isArray(entries) ? entries : [];
	const locationRate = layawayLocationCombinedRate(location);
	const fflBossRate = layawayLocationFflRate(location);

	let subtotal = 0;
	let taxableMerchandiseSum = 0;
	let nonTaxableMerchandiseSum = 0;
	const taxSummaryMap = {};

	safeEntries.forEach((entry) => {
		const quantity = layawayEntryQuantity(entry);
		const unitCost = layawayEntryUnitCost(entry);
		const discount = layawayEntryDiscount(entry);
		const lineAmount = Math.max(0, (unitCost * quantity) - discount);
		subtotal += lineAmount;

		const taxable = layawayEntryTaxable(entry);

		if (!taxable || lineAmount <= 0) {
			if (!taxable && lineAmount > 0) {
				nonTaxableMerchandiseSum += lineAmount;
			}
			return;
		}
		taxableMerchandiseSum += lineAmount;

		let rate = locationRate;
		let group = 'Default Group';

		if (entry?.type === 'consignment') {
			group = 'Consignments';
			rate = locationRate;
		} else if (entry?.type === 'product') {
			group = 'FFLBoss';
			if (isFflLinked && fflBossRate > 0) rate = fflBossRate;
		} else if (entry?.type === 'service') {
			group = 'Services';
			rate = locationRate;
		} else {
			const skuClass = layawayEntrySkuClass(entry);
			const shouldOverride = layawayBool(skuClass?.should_override);
			const overrideRate = layawayNum(skuClass?.tax_override);
			if (shouldOverride && overrideRate > 0) rate = overrideRate;
			group = skuClass?.name || 'Default Group';
		}

		const key = `${group}|${rate.toFixed(4)}`;
		if (!taxSummaryMap[key]) {
			taxSummaryMap[key] = {
				group,
				rate,
				amount: 0,
				quantity: 0,
				tax_raw: 0,
			};
		}
		taxSummaryMap[key].amount += lineAmount;
		taxSummaryMap[key].quantity += quantity;
		taxSummaryMap[key].tax_raw += (lineAmount * rate / 100);
	});

	let taxSummary = Object.values(taxSummaryMap).map((row) => ({
		group: row.group,
		rate: row.rate,
		amount: row.amount,
		quantity: row.quantity,
		tax: Math.round((row.tax_raw + Number.EPSILON) * 100) / 100,
	}));

	const taxAmount = safeEntries.length > 0
		? Math.round((taxSummary.reduce((s, row) => s + row.tax, 0) + Number.EPSILON) * 100) / 100
		: 0;
	const subTotalAmount = Math.round((subtotal + Number.EPSILON) * 100) / 100;
	const taxableSubtotal = Math.round((taxableMerchandiseSum + Number.EPSILON) * 100) / 100;
	const nonTaxableSubtotal = Math.round((nonTaxableMerchandiseSum + Number.EPSILON) * 100) / 100;

	const bucketTaxTotal = taxSummary.reduce((s, row) => s + row.tax, 0);
	const bucketDiff = Math.round((taxAmount - bucketTaxTotal + Number.EPSILON) * 100) / 100;
	if (bucketDiff !== 0 && taxSummary.length > 0) {
		const last = taxSummary[taxSummary.length - 1];
		last.tax = Math.round((last.tax + bucketDiff + Number.EPSILON) * 100) / 100;
	}

	const amount = Math.round(((subTotalAmount + taxAmount) + Number.EPSILON) * 100) / 100;
	const dep = Math.max(0, layawayNum(deposit));
	const remaining = Math.round((amount - dep + Number.EPSILON) * 100) / 100;

	return {
		sub_total_amount: subTotalAmount,
		taxable_subtotal: taxableSubtotal,
		non_taxable_subtotal: nonTaxableSubtotal,
		tax_amount: taxAmount,
		tax_rate: Math.round((locationRate + Number.EPSILON) * 10000) / 10000,
		amount,
		remaining,
		tax_json: JSON.stringify(taxSummary),
	};
}

class AddLayaway extends Framework.Components.Object {
	constructor(props) {
		/**
		 *  @typedef {Layaways_Object} this.state
		 */
		super(props, Types.Layaways._name);
		this.state.items = [];
		this.state.products = [];
		this.state.entries = [];
		this.state.sub_total_amount = 0.00;
		this.state.tax_amount = 0.00;
		this.state.amount = 0.00;
		this.state.deposit = 0.00;
		this.state.remaining = 0.00;
		this.afterCustomerSave = this.setNewObject('customer');
		const initialTax = computeLayawayTaxTotals(
			this.state.entries,
			props.rootState.location,
			!!props.rootState.ffl_boss_linked,
			this.state.deposit
		);
		Object.assign(this.state, initialTax);
	}
	// addItems = async (items) => {
	// 	let total = parseFloat(this.state.sub_total_amount);
	// 	let entries = this.state.items
	// 	if (typeof this.state.items !== 'undefined' && Array.isArray(items)) {
	// 		items.forEach(item => {
	// 			let found = entries.find((entry)=> entry.item === item.pkey)
	// 			if (!found) {
	// 				entries.push({item:item.pkey, items:item})
	// 			} else {
	// 				let index = entries.findIndex((entry)=> entry.item === item.pkey)
	// 				entries[index].items.quantity += item.quantity
	// 			}
	// 			total += parseFloat(item.skus.price_retail * item.quantity)
	// 		})
	// 		this.setState({items: entries, sub_total_amount: total});
	// 	}
	// }
	//
	// addProducts = async (products) => {
	// 	let total = parseFloat(this.state.sub_total_amount);
	// 	if (typeof this.state.products !== 'undefined' && Array.isArray(products)) {
	// 		let entries = [...this.state.products, ...products.map(
	// 			product => {
	// 				total += parseFloat(product.price_list);
	// 				return {
	// 					product: product.pkey,
	// 					products: product
	// 				}
	// 			}
	// 		)];
	// 		this.setState({products: entries, sub_total_amount: total});
	// 	}
	// }
	//
	// deleteItem = (entry, item) => {
	// 	let total = parseFloat(this.state.sub_total_amount);
	// 	if (!entry) {
	// 		if (typeof this.state.items !== 'undefined') {
	// 			let entries = this.state.items.filter(entry => entry.item !== item.pkey);
	// 			total -= parseFloat(item.skus.price_retail * item.quantity);
	// 			this.setState({items: entries, sub_total_amount: total});
	// 		}
	// 	}
	// }
	//
	// deleteProduct = (entry, product) => {
	// 	let total = parseFloat(this.state.sub_total_amount);
	// 	if (!entry) {
	// 		if (typeof this.state.products !== 'undefined') {
	// 			let entries = this.state.products.filter(entry => entry.product !== product.pkey);
	// 			total -= parseFloat(product.price_list);
	// 			this.setState({products: entries, sub_total_amount: total});
	// 		}
	// 	}
	// }
	//
	// updateItem = (item, newEntry = null) => {
	// 	if (typeof this.state.items !== 'undefined') {
	// 		let entries = this.state.items.map(entry => {
	// 			if (entry.item === item.pkey) {
	// 				entry.item = item.pkey;
	// 				entry.items = item;
	// 			}
	// 			return entry;
	// 		});
	// 		this.setState({items: entries});
	// 	}
	// 	if (!newEntry) {
	// 		this.props.reloadPage();
	// 	}
	// }
	//
	// updateProduct = (product, newEntry = null) => {
	// 	if (typeof this.state.products !== 'undefined') {
	// 		let entries = this.state.products.map(entry => {
	// 			if (entry.product === product.pkey) {
	// 				entry.product = product.pkey;
	// 				entry.products = product;
	// 			}
	// 			return entry;
	// 		});
	// 		this.setState({product: entries});
	// 	}
	// 	if (!newEntry) {
	// 		this.props.reloadPage();
	// 	}
	// }

	handleToggleItemModel = (forItem) => {
		if (typeof this.state[forItem] === 'undefined') {
			this.state[forItem] = false;
		}
		return () => {
			this.setState({[forItem]: true});
		};
	}

	handleReportModalClose = (forItem) => () => {
		this.setState({ [forItem]: false });
	}

	handleAddPayment = (payment) => {
		const cash_amount = payment.cash_amount;
		const check_amount = payment.check_amount;
		const zelle_amount = payment.zelle_amount;
		const deposit = layawayNum(payment.cash_amount) + layawayNum(payment.check_amount) + layawayNum(payment.zelle_amount);
		this.setState((prev) => ({
			...prev,
			cash_amount,
			check_amount,
			zelle_amount,
			deposit,
			...computeLayawayTaxTotals(
				prev.entries,
				this.props.rootState.location,
				!!this.props.rootState.ffl_boss_linked,
				deposit
			),
		}));
	}

	handleLayawayEntriesChange = (entries) => {
		this.setState((prev) => ({
			...prev,
			entries,
			...computeLayawayTaxTotals(
				entries,
				this.props.rootState.location,
				!!this.props.rootState.ffl_boss_linked,
				prev.deposit
			),
		}));
	}

	handleScan = (data) => {
		this.fetchScanData(data).then((r) => r);
	}

	handleError = (err) => {
		console.log('Scan Error', err);
	}

	fetchScanData = async (data) => {
		let productType = data.split('-')[0];
		data = data.split('-')[1];
		// If scanned item is an Item
		if (productType === 'I') {
			try {
				const response = await $.ajax({
					type: "GET",
					url: APPCONFIG.settings.apiURL() + '/' + APPCONFIG.settings.apiVersion + '/inventories/' + data,
				});
				const inventory = response.response;
				const proceed = await guardOpenWorkOrderBeforeAdd(
					inventory?.pkey,
					inventory?.skus?.name || inventory?.skus?.sku || 'This item'
				);
				if (!proceed) {
					return;
				}
				API.ToastId = toast.info(inventory.skus.name + ' added to layaway.', {
					position: toast.POSITION.TOP_RIGHT
				});
				inventory.quantity = 1;
				if (typeof this.addItems === 'function') {
					await this.addItems([inventory]);
				}
			} catch (xhr) {
				const errorMessage = xhr?.responseJSON?.response === 'Product is already in cart'
					? xhr.responseJSON.response
					: 'We were unable to find that product you requested on your account.';
				API.ToastId = toast.error(errorMessage, {
					position: toast.POSITION.TOP_CENTER
				});
			}
		// If scanned item is a Product (FFL Boss)
		} else {
			if (!this.props.rootState.ffl_boss_linked) {
				toast.error('FFL Boss is not linked to this account.');
				return;
			}
			$.ajax({
				type: "GET",
				url: APPCONFIG.settings.apiURL() + '/' + APPCONFIG.settings.apiVersion + '/inventories/connected?search==pkey:' + data + ";",
				success: (data) => {
					API.ToastId = toast.info(data.response.items[0].manufacturers.name + ' ' + data.response.items[0].model + ' added to layaway.', {
						position: toast.POSITION.TOP_RIGHT
					});
					data.response.quantity = 1;
					this.addProducts([data.response.items[0]]).then((r) => r);
				},
				error: function (xhr, status, error) {
					//TODO: change this error msg
					let errorMessage = xhr.responseJSON.response === 'Product is already in cart' ? xhr.responseJSON.response : 'We were unable to find that product you requested on your account.'
					API.ToastId = toast.error(errorMessage, {
						position: toast.POSITION.TOP_CENTER
					});
				}
			});
		}
	}

	reducer = (acc, curr) => {
		return acc + (parseFloat((curr.price ? curr.price : curr.cost ? curr.cost : curr.information.skus?.price_retail ? curr.information.skus.price_retail : curr.information.price_list) - parseFloat(curr.discount_amount ? curr.discount_amount : 0)) * (curr.quantity ? curr.quantity : curr.information.quantity ? curr.information.quantity : 1))
	}

	handleSubmit = async (ev) => {
		if (this.state.estimated_date) {
			const parsed = new Date(this.state.estimated_date);
			if (!Number.isNaN(parsed.getTime())) {
				const today = new Date();
				today.setHours(0, 0, 0, 0);
				parsed.setHours(0, 0, 0, 0);
				if (parsed < today) {
					API.Toast.error('Estimated payoff date cannot be in the past.', { position: API.Toast.POSITION.TOP_RIGHT });
					return;
				}
			}
		}
		if (this.state.tax_json && typeof this.state.tax_json !== 'string') {
			this.state.tax_json = JSON.stringify(this.state.tax_json);
		}
		await super.handleSubmit(ev);
	}

	form = () => {
		/**
		 * @type {Layaways_Properties} properties
		 */
		let properties = this.model.properties;
		/**
		 *  @type {Layaways_Object} object
		 */
		let object = this.state;
		let { location } = this.props.rootState;
		const locationRate = layawayLocationCombinedRate(location);

		let taxSummary = [];
		if (object.tax_json && typeof object.tax_json === 'string') {
			try {
				const parsed = JSON.parse(object.tax_json);
				if (Array.isArray(parsed)) {
					taxSummary = parsed;
				} else if (parsed?.original?.buckets) {
					taxSummary = parsed.original.buckets;
				} else if (parsed?.current?.buckets) {
					taxSummary = parsed.current.buckets;
				}
			} catch (e) {
				taxSummary = [];
			}
		}
		const defaultTaxRows = taxSummary.filter(
			(row) => Math.abs(parseFloat(row.rate) - locationRate) <= 0.0001
		);
		const customTaxRows = taxSummary.filter(
			(row) => Math.abs(parseFloat(row.rate) - locationRate) > 0.0001
		);
		const displayTaxSummary = [];
		const defaultAmount = defaultTaxRows.reduce((s, row) => s + Number(row.amount || 0), 0);
		const defaultTax = Math.round(defaultTaxRows.reduce((s, row) => s + Number(row.tax || 0), 0) * 100) / 100;
		if (defaultAmount > 0 || (customTaxRows.length === 0 && object.tax_amount > 0)) {
			displayTaxSummary.push({
				group: 'Default Group',
				rate: locationRate,
				amount: defaultAmount > 0 ? defaultAmount : object.sub_total_amount,
				tax: defaultAmount > 0 ? defaultTax : object.tax_amount,
			});
		}
		displayTaxSummary.push(...customTaxRows);

		return (
			<>
				<fieldset>
					<div className={'row'}>
						<div className={'col-lg-5'}>
							<h4 className="section-title">Layaway Details</h4>
							<CustomerComponent
								locations={this.props.locations || []}
								object={object}
								properties={properties}
								shouldLoad={true}
								history={this.props.history}
								onTextUpdate={this.handleTextFieldPropertyUpdate}
								onSelectUpdate={this.handleSelectPropertyUpdate}
								afterCustomerSave={this.afterCustomerSave}
								key={object.customers ? JSON.stringify(object.customers) : "customer_layaway_add"}
							/>
							<div className={'row'}>
								<div className="col">
									<div className={"form-group"}>
										<Framework.Elements.TextField
											name={"description"}
											multiline={4}
											label={"Layaway Description (Shown On Receipt)"}
											placeholder={"Public description about layaway"}
											value={object.description}
											error={object.description_error}
											property={properties.description}
											update={this.handleTextFieldPropertyUpdate}
										/>
									</div>
								</div>
							</div>
							<div className={'row'}>
								<div className="col">
									<div className={"form-group"}>
										<Framework.Elements.TextField
											name={"layaway_notes"}
											multiline={4}
											label={"Layaway Notes (Shown On Receipt)"}
											placeholder={"Public notes about layaway"}
											value={object.layaway_notes}
											error={object.layaway_notes_error}
											property={properties.layaway_notes}
											update={this.handleTextFieldPropertyUpdate}
										/>
									</div>
								</div>
							</div>
							<div className={'row'}>
								<div className="col">
									<div className={"form-group"}>
										<Framework.Elements.TextField
											multiline={4}
											label={"Customer Notes (Not Shown On Receipt)"}
											placeholder={"Private notes about layaway"}
											value={object.customer_notes}
											error={object.customer_notes_error}
											property={properties.customer_notes}
											update={this.handleTextFieldPropertyUpdate}
										/>
									</div>
								</div>
							</div>
							<div className={'row'}>
								<div className="col">
									<div className={"form-group"}>
										<Framework.Elements.Date
											label={"Estimated Payoff Date"}
											value={object.estimated_date}
											error={object.estimated_date_error}
											property={properties.estimated_date}
											update={this.handleTextFieldPropertyUpdate}
										/>
									</div>
								</div>
							</div>

							<div className=" invoice-sum text-right">
								<ul className="list-unstyled">
									<li>Sub Total: $ {parseFloat(object.sub_total_amount).toFixed(2)}</li>
									{displayTaxSummary.length > 0 ? (
										<>
											{displayTaxSummary.map((row, idx) => (
												<li key={`lay-tax-${idx}-${row.group}-${row.rate}`}>
													Tax: ${parseFloat(row.amount).toFixed(2)} @ {parseFloat(row.rate).toFixed(3)}%
													{row.group && row.group !== 'Default Group'
														? row.group === 'FFLBoss' &&
														  Math.abs(parseFloat(row.rate) - locationRate) > 0.0001
															? ' (FFL)'
															: row.group === 'FFLBoss'
																? ''
																: row.group === 'Consignments'
																	? ' (Consignment)'
																	: ` (${row.group})`
														: ''}
													<span style={{float: "right"}}>&nbsp;&nbsp;$ {parseFloat(row.tax).toFixed(2)}</span>
												</li>
											))}
											{displayTaxSummary.length > 1 &&
												<li><strong>Total Tax: $ {object.tax_amount.toFixed(2)}</strong></li>
											}
										</>
									) : (
										object.tax_amount > 0
											? <li>Tax: $ {parseFloat(object.sub_total_amount).toFixed(2)} @ {parseFloat(object.tax_rate).toFixed(3)}% <span style={{float: "right"}}>$ {object.tax_amount.toFixed(2)}</span></li>
											: <li>Tax $0.00:</li>
									)}
									<li><strong>Total:
										$ {isNaN(object.amount) ? "0.00" : parseFloat(object.amount).toFixed(2)}</strong>
									</li>
								</ul>
							</div>
							<div className="invoice-sum text-center">
								<i>Start the layaway to make a payment</i>
							</div>

						</div>
						{/*TODO: handle scanning*/}
						<div className={'col-lg-7'}>
							<Elements.LayawayItems
								updateSubTotal={() => {
									/* Subtotal is derived from entries + tax in parent state */
								}}
								updateEntries={this.handleLayawayEntriesChange}
							/>
						</div>
					</div>
				</fieldset>
			</>
		)
	}
}

export default Framework.Components.withRootState(AddLayaway);
